import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrgHall } from "@/hooks/useOrgHall";
import { formatPrice } from "@/utils/formatPrice";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import BookingDetailModal from "@/components/booking/BookingDetailModal";

type StatusFilter = "all" | "upcoming" | "completed" | "cancelled";

export default function DashboardBookingsToday() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hall } = useOrgHall();
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  const today = new Date().toISOString().split("T")[0];
  const now = new Date().toISOString();

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["dashboard-bookings-today", today],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, start_time, end_time, status, is_open_match, price_total, user_id, court_id, courts(name), profiles:user_id(full_name, avatar_url)"
        )
        .gte("start_time", today + "T00:00:00")
        .lt("start_time", today + "T23:59:59")
        .order("start_time", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: paymentMap = {} } = useQuery({
    queryKey: ["dashboard-bookings-today-payments", today],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_payments")
        .select("booking_id, status")
        .in(
          "booking_id",
          bookings.map((b) => b.id)
        );
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const p of data ?? []) {
        // If any payment is paid, mark booking as paid
        if (p.status === "paid" || !map[p.booking_id]) {
          map[p.booking_id] = p.status;
        }
      }
      return map;
    },
    enabled: bookings.length > 0,
  });

  const filtered = bookings.filter((b) => {
    if (filter === "all") return true;
    if (filter === "cancelled") return b.status === "cancelled";
    if (filter === "completed") return b.end_time < now && b.status === "confirmed";
    if (filter === "upcoming") return b.start_time >= now && b.status === "confirmed";
    return true;
  });

  const confirmedCount = bookings.filter((b) => b.status === "confirmed").length;
  const cancelledCount = bookings.filter((b) => b.status === "cancelled").length;

  function getRowColor(booking: (typeof bookings)[0]) {
    if (booking.status === "cancelled") return "";
    if (booking.is_open_match) return "border-l-4 border-l-yellow-400";
    if (booking.user_id === user?.id) return "border-l-4 border-l-blue-400";
    const paid = paymentMap[booking.id] === "paid";
    return paid ? "border-l-4 border-l-green-500" : "border-l-4 border-l-red-500";
  }

  function getPaymentBadge(booking: (typeof bookings)[0]) {
    if (booking.is_open_match) return <Badge variant="outline">Aktivitet</Badge>;
    if (booking.status === "cancelled")
      return <Badge variant="secondary">Avbokad</Badge>;
    const paid = paymentMap[booking.id] === "paid";
    return paid ? (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Betald</Badge>
    ) : (
      <Badge variant="destructive">Obetald</Badge>
    );
  }

  const filters: { key: StatusFilter; label: string }[] = [
    { key: "all", label: t("common.all", "Alla") },
    { key: "upcoming", label: t("dashboard.upcoming", "Kommande") },
    { key: "completed", label: t("dashboard.completed", "Avslutade") },
    { key: "cancelled", label: t("dashboard.cancelled", "Avbokade") },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/manager/dashboard"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold">
          {t("dashboard.bookingsToday", "Bokningar idag")} ({bookings.length})
        </h1>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Bookings list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t("dashboard.noBookings", "Inga bokningar att visa")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((booking) => {
            const profile = booking.profiles as unknown as {
              full_name: string | null;
              avatar_url: string | null;
            } | null;
            const court = booking.courts as unknown as { name: string } | null;
            return (
              <Card
                key={booking.id}
                className={cn(
                  "cursor-pointer hover:shadow-md transition-shadow",
                  getRowColor(booking)
                )}
                onClick={() => setSelectedBookingId(booking.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="text-sm font-mono whitespace-nowrap">
                        {format(new Date(booking.start_time), "HH:mm")}–
                        {format(new Date(booking.end_time), "HH:mm")}
                      </span>
                      <span className="text-sm font-medium truncate">
                        {court?.name ?? "—"}
                      </span>
                      <span className="text-sm text-muted-foreground truncate">
                        {profile?.full_name ?? t("common.unknown", "Okänd")}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-medium">
                        {booking.price_total != null
                          ? formatPrice(booking.price_total)
                          : "—"}
                      </span>
                      {getPaymentBadge(booking)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <div className="text-sm text-muted-foreground">
        {confirmedCount} {t("dashboard.booked", "bokade")} / {cancelledCount}{" "}
        {t("dashboard.cancelledLower", "avbokade")}
      </div>

      {/* Booking detail modal */}
      <BookingDetailModal
        open={!!selectedBookingId}
        onOpenChange={(open) => {
          if (!open) setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId ?? ""}
      />
    </div>
  );
}
